import type {DefaultOptions} from '@tanstack/react-query'
import {ApiError} from '@/api/client'

/**
 * Global TanStack Query defaults applied app-wide via the root
 * {@code QueryClient} in {@code main.tsx}. Extracted into a factory so the
 * defaults can be unit-tested without spinning up the whole app.
 *
 * <h4>No caching</h4>
 * {@code staleTime: 0} — every query observer mount refetches. The cost is
 * a duplicate request under React-StrictMode double-mount; the benefit is
 * that no list page ever shows stale rows after a mutation elsewhere.
 * Issue 388, following the user mandate from HQG round 1 of {@code b2df6458c9}:
 * "I don't want caching at all in the app". Predecessors (issues 244, 369)
 * tried to tune the window and kept regressing; the window is removed.
 */
export function createAppQueryDefaults(): DefaultOptions {
    return {
        queries: {
            staleTime: 0,
            retry: (failureCount, error) => {
                // 4xx errors indicate client/request problems — retrying won't
                // help. This includes 400, 401, 403, 404, 409. Other 4xx codes
                // (e.g. 429 rate-limit) may benefit from retries, but that's
                // worth handling explicitly when it comes up.
                if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
                return failureCount < 1
            },
        },
    }
}
